import { Input, Select, type SelectOption } from '../../ui';
import { isNumericFieldType } from '../../features/templates/templateModel';
import type { FieldDef } from '../../features/templates/types';
import type { EstimateInputValue } from '../../features/estimation/types';

export interface EstimateFieldInputProps {
  field: FieldDef;
  value: EstimateInputValue;
  onChange: (value: EstimateInputValue) => void;
}

const BOOL_OPTIONS: SelectOption[] = [
  { value: 'false', label: 'No' },
  { value: 'true', label: 'Yes' },
];

/**
 * Renders the correct editor for a single template {@link FieldDef}:
 *   - number/factor/multiplier/threshold -> numeric <input> (with min/max)
 *   - enum                               -> <Select> of the field's options
 *   - boolean                            -> Yes/No <Select>
 *   - text                               -> text <input>
 *
 * Fully controlled: it never coerces types itself beyond what the input element
 * requires — coercion to the field's canonical type happens in the engine.
 */
export function EstimateFieldInput({
  field,
  value,
  onChange,
}: EstimateFieldInputProps) {
  const hint = field.helpText;

  if (isNumericFieldType(field.type)) {
    return (
      <Input
        type="number"
        label={field.label || field.key}
        hint={hint}
        min={field.min}
        max={field.max}
        step="any"
        value={value === undefined || value === '' ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? 0 : Number(raw));
        }}
      />
    );
  }

  if (field.type === 'enum') {
    const options: SelectOption[] = (field.options ?? []).map((o) => ({
      value: o,
      label: o,
    }));
    return (
      <div className="flex flex-col gap-1.5">
        <Select
          label={field.label || field.key}
          options={options}
          placeholder={field.options?.length ? undefined : 'No options'}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        />
        {hint && <p className="text-xs text-zinc-500">{hint}</p>}
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <Select
        label={field.label || field.key}
        options={BOOL_OPTIONS}
        value={value === true || value === 'true' ? 'true' : 'false'}
        onChange={(e) => onChange(e.target.value === 'true')}
      />
    );
  }

  // text
  return (
    <Input
      type="text"
      label={field.label || field.key}
      hint={hint}
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default EstimateFieldInput;
